import { useState, FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { trackFormSubmission } from '../../../utils/analytics';
import './NotifyMeModal.css';

interface NotifyMeModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  productId: string;
}

const NotifyMeModal: React.FC<NotifyMeModalProps> = ({
  isOpen,
  onClose,
  productName,
  productId,
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Track form submission attempt
    trackFormSubmission('notify_modal', 'attempt', undefined);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address');
      trackFormSubmission('notify_modal', 'error', 'Invalid email address');
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch('https://formspree.io/f/xblzpdpj', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          productName,
          productId,
          message: `Notification request for ${productName} (ID: ${productId})`,
        }),
      });

      if (response.ok) {
        setStatus('success');
        setEmail('');
        trackFormSubmission('notify_modal', 'success', undefined);
      } else {
        setStatus('error');
        setErrorMessage('Failed to save your email. Please try again.');
        trackFormSubmission('notify_modal', 'error', 'Server error');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
      trackFormSubmission('notify_modal', 'error', 'Network error');
    }
  };

  const handleClose = () => {
    setEmail('');
    setStatus('idle');
    setErrorMessage('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          <motion.div
            className="modal"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', duration: 0.5 }}
          >
            <button className="modal__close" onClick={handleClose}>
              ✕
            </button>

            {status === 'success' ? (
              <div className="modal__success">
                <div className="modal__success-icon">✓</div>
                <h3 className="modal__success-title">You're on the list!</h3>
                <p className="modal__success-message">
                  We'll notify you when {productName} is back in stock.
                </p>
                <button className="modal__button" onClick={handleClose}>
                  Got it!
                </button>
              </div>
            ) : (
              <>
                <h3 className="modal__title">Notify Me When Available</h3>
                <p className="modal__subtitle">
                  {productName} is currently out of stock. Enter your email and
                  we'll notify you as soon as it's back!
                </p>

                <form className="modal__form" onSubmit={handleSubmit}>
                  <div className="modal__form-group">
                    <label htmlFor="email" className="modal__label">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      className={`modal__input ${
                        status === 'error' ? 'modal__input--error' : ''
                      }`}
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setStatus('idle');
                        setErrorMessage('');
                      }}
                      required
                    />
                    {status === 'error' && (
                      <span className="modal__error">{errorMessage}</span>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="modal__button"
                    disabled={status === 'loading'}
                  >
                    {status === 'loading' ? 'Submitting...' : 'Notify Me'}
                  </button>
                </form>

                <p className="modal__privacy">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NotifyMeModal;
